/**
 * DeleteStepDialog — choose HOW to delete a journey step.
 *
 * Before 2026-11-04 the trash icon asked "Delete this step and all its
 * children?" and deleted the whole subtree (the parent_step_id foreign key
 * cascades). A journey owner who only wanted to remove a tag step lost every
 * email under it. This dialog offers two explicit choices when the step has
 * steps under it:
 *
 *   1. Delete this step only (keep the steps under it) — server RPC
 *      delete_sequence_step_keep_children (mig 1720). The server decides
 *      whether that is safe; when it is not, the option is disabled and the
 *      server's reason is shown.
 *   2. Delete this step and everything under it — the old behaviour.
 *
 * A step with nothing under it gets a plain confirmation.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../common/Modal";
import Button from "../common/Button";
import type { SequenceStep } from "../../types/sequence.types";
import {
  deleteSequenceStepKeepChildren,
  type StepDeleteKeepChildrenPlan,
} from "../../services/sequences.service";

export type DeleteStepChoice = "keep_children" | "with_children";

interface DeleteStepDialogProps {
  step: SequenceStep | null;
  onClose: () => void;
  /** Runs the chosen delete; resolves when done, rejects with a readable message. */
  /** `seenIds` = every step this dialog showed under the step (empty for a leaf). The server
   *  refuses (SUBTREE_CHANGED) if the step's subtree is no longer exactly that (mig 1721). */
  onConfirm: (
    step: SequenceStep,
    choice: DeleteStepChoice,
    seenIds: string[],
  ) => Promise<void>;
}

/** Ids of every step below `step`, at any depth (what the dialog shows). */
export function descendantIds(step: SequenceStep): string[] {
  const out: string[] = [];
  for (const c of step.children ?? []) {
    out.push(c.id, ...descendantIds(c));
  }
  return out;
}

/** Every step below `step`, at any depth. */
export function countDescendants(step: SequenceStep): number {
  let n = 0;
  for (const c of step.children ?? []) {
    n += 1 + countDescendants(c);
  }
  return n;
}

function describeMode(
  plan: StepDeleteKeepChildrenPlan,
  step: SequenceStep,
): string {
  const only = (step.children ?? [])[0];
  const childName = only ? `"${only.name}"` : "The step under it";
  switch (plan.mode) {
    case "child_replaces_step":
      return step.parent_step_id
        ? `${childName} moves up into this step's place. People who would have reached this step go straight to ${childName}, under the same condition and in the same order.`
        : `${childName} becomes the first step of the journey.`;
    case "conditional_child_moves_up":
      return `${childName} moves up one level and keeps its own condition. It is checked after the other branches at that level, just as this step was.`;
    case "split_moves_up":
      return `The ${plan.children} branches under this step move up one level, with their conditions and order unchanged.`;
    default:
      return "";
  }
}

const DeleteStepDialog: React.FC<DeleteStepDialogProps> = ({
  step,
  onClose,
  onConfirm,
}) => {
  const [choice, setChoice] = React.useState<DeleteStepChoice | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setChoice(null);
    setBusy(false);
    setError(null);
  }, [step?.id]);

  const childCount = step?.children?.length ?? 0;
  const descendants = step ? countDescendants(step) : 0;

  // Ask the server (dry run, no write) whether "this step only" is safe.
  const planQuery = useQuery({
    queryKey: ["step-delete-plan", step?.id, childCount],
    queryFn: () => deleteSequenceStepKeepChildren(step!.id, true),
    enabled: !!step && childCount > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const plan = planQuery.data;
  const keepAllowed = plan?.allowed === true;

  if (!step) {
    return null;
  }

  const run = async (c: DeleteStepChoice) => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(step, c, descendantIds(step));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  // ---- a step with nothing under it ----
  if (childCount === 0) {
    return (
      <Modal isOpen onClose={onClose} title="Delete step" size="md">
        <div className="space-y-4" data-testid="delete-step-dialog">
          <p className="text-sm text-gray-700">
            Delete <strong>{step.name}</strong>? There are no steps under it.
            This cannot be undone.
          </p>
          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => run("with_children")}
              disabled={busy}
              data-testid="delete-step-confirm"
            >
              {busy ? "Deleting…" : "Delete step"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // ---- a step with steps under it: two explicit choices ----
  return (
    <Modal isOpen onClose={onClose} title="Delete step" size="lg">
      <div className="space-y-4" data-testid="delete-step-dialog">
        <p className="text-sm text-gray-700">
          <strong>{step.name}</strong> has {childCount}{" "}
          {childCount === 1 ? "step" : "steps"} directly under it
          {descendants > childCount ? ` (${descendants} in total)` : ""}. What
          should happen to them?
        </p>

        <label
          className={`flex gap-3 rounded-md border p-3 ${
            keepAllowed
              ? "cursor-pointer border-gray-300 hover:bg-gray-50"
              : "border-gray-200 bg-gray-50"
          } ${choice === "keep_children" ? "ring-2 ring-blue-500" : ""}`}
        >
          <input
            type="radio"
            name="delete-step-choice"
            className="mt-1"
            checked={choice === "keep_children"}
            disabled={!keepAllowed || busy}
            onChange={() => setChoice("keep_children")}
            data-testid="delete-step-keep-children"
          />
          <span className="space-y-1">
            <span className="block text-sm font-medium text-gray-900">
              Delete this step only (keep the{" "}
              {descendants === 1 ? "step" : `${descendants} steps`} under it)
            </span>
            {planQuery.isLoading && (
              <span className="block text-sm text-gray-500">
                Checking whether this is safe…
              </span>
            )}
            {planQuery.isError && (
              <span className="block text-sm text-red-700" role="alert">
                Could not check: {(planQuery.error as Error).message}
              </span>
            )}
            {plan && keepAllowed && (
              <>
                <span className="block text-sm text-gray-600">
                  {describeMode(plan, step)}
                </span>
                {(plan.warnings ?? []).map((w) => (
                  <span
                    key={w}
                    className="block text-sm text-amber-700"
                    data-testid="delete-step-warning"
                  >
                    {w}
                  </span>
                ))}
              </>
            )}
            {plan && !keepAllowed && (
              <span
                className="block rounded bg-amber-50 p-2 text-sm text-amber-800"
                data-testid="delete-step-keep-refused"
              >
                Not available here: {plan.message}
              </span>
            )}
          </span>
        </label>

        <label
          className={`flex cursor-pointer gap-3 rounded-md border border-gray-300 p-3 hover:bg-gray-50 ${
            choice === "with_children" ? "ring-2 ring-red-500" : ""
          }`}
        >
          <input
            type="radio"
            name="delete-step-choice"
            className="mt-1"
            checked={choice === "with_children"}
            disabled={busy}
            onChange={() => setChoice("with_children")}
            data-testid="delete-step-with-children"
          />
          <span className="space-y-1">
            <span className="block text-sm font-medium text-gray-900">
              Delete this step and everything under it ({descendants + 1} steps)
            </span>
            <span className="block text-sm text-gray-600">
              Removes this step and every step below it, including any emails.
              This cannot be undone.
            </span>
          </span>
        </label>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => choice && run(choice)}
            disabled={!choice || busy}
            data-testid="delete-step-confirm"
          >
            {busy
              ? "Deleting…"
              : choice === "with_children"
                ? `Delete ${descendants + 1} steps`
                : "Delete this step only"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteStepDialog;
